/* Native forms and option links remain usable without these enhancements. */
if (!customElements.get('szponty-product')) {
  customElements.define('szponty-product', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      this.horizontal = matchMedia('(max-width: 1199px)');
      this.gallery = this.querySelector('.pdp-media');
      this.photos = [...this.querySelectorAll('.pdp-photo')];
      this.dialog = this.querySelector('.pdp-lightbox');
      this.imageButtons = [...this.querySelectorAll('.pdp-image-frame')];
      const on = (target, event, callback, options = {}) => target?.addEventListener(event, callback, { ...options, signal: this.events.signal });
      this.on = on;
      on(this, 'click', event => {
        const link = event.target.closest('[data-option-link]');
        if (!link || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const url = new URL(link.href);
        if (url.pathname !== new URL(this.dataset.productUrl, location.origin).pathname) return;
        event.preventDefault();
        this.selectVariant(link, url);
      });
      this.selectionLocation = location.pathname + location.search;
      on(window, 'popstate', () => {
        // Hash navigation (including the skip link) must not reload the page.
        if (this.selectionLocation !== location.pathname + location.search) location.reload();
      });
      on(this.gallery, 'scroll', () => this.updateGallery(), { passive: true });
      on(this.gallery, 'keydown', event => {
        if (!this.horizontal.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        if (event.target !== this.gallery && !event.target.closest('.pdp-image-frame')) return;
        event.preventDefault();
        this.moveGallery(event.key === 'ArrowRight' ? 1 : -1);
      });
      on(this.querySelector('[data-gallery-previous]'), 'click', () => this.moveGallery(-1));
      on(this.querySelector('[data-gallery-next]'), 'click', () => this.moveGallery(1));
      const information = this.querySelector('.pdp-information');
      const updateLayout = () => {
        this.updateGallery();
        information.classList.toggle('is-sticky', !this.horizontal.matches && information.offsetHeight + 48 <= innerHeight);
      };
      on(window, 'resize', updateLayout);
      this.resizeObserver = new ResizeObserver(updateLayout);
      this.resizeObserver.observe(information);
      if (this.gallery) this.resizeObserver.observe(this.gallery);
      this.gallery?.classList.add('is-enhanced');
      this.setupLightbox();
      updateLayout();
      this.revealVariantMedia(false);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.variantRequest?.abort();
      this.resizeObserver?.disconnect();
      if (this.dialog?.open) this.dialog.close();
    }

    positionLabel(first, last, total) {
      return this.dataset.positionLabel
        .replace('[first]', String(first))
        .replace('[last]', first === last ? '' : '–' + last)
        .replace('[total]', String(total));
    }

    galleryState() {
      if (!this.photos.length) return { visible: 1, stride: 0, index: 0 };
      const visible = this.horizontal.matches ? Math.max(1, Math.round(this.gallery.clientWidth / this.photos[0].offsetWidth)) : 2;
      const stride = this.photos.length > 1 ? this.photos[1].offsetLeft - this.photos[0].offsetLeft : this.gallery.clientWidth;
      const index = this.horizontal.matches && stride > 0 ? Math.max(0, Math.round(this.gallery.scrollLeft / stride)) : 0;
      return { visible, stride, index };
    }

    updateGallery() {
      if (!this.gallery) return;
      const { visible, index } = this.galleryState();
      const canScroll = this.horizontal.matches && this.gallery.scrollWidth > this.gallery.clientWidth + 1;
      this.gallery.tabIndex = canScroll ? 0 : -1;
      this.querySelector('.gallery-navigation').hidden = !canScroll;
      if (!this.photos.length) return;
      const position = this.querySelector('[data-gallery-position]');
      const label = this.positionLabel(index + 1, Math.min(this.photos.length, index + visible), this.photos.length);
      if (position.textContent !== label) position.textContent = label;
      this.querySelector('[data-gallery-previous]').setAttribute('aria-disabled', String(index === 0));
      this.querySelector('[data-gallery-next]').setAttribute('aria-disabled', String(this.gallery.scrollLeft >= this.gallery.scrollWidth - this.gallery.clientWidth - 1));
    }

    moveGallery(step) {
      const { visible, stride, index } = this.galleryState();
      const next = Math.max(0, Math.min(this.photos.length - visible, index + step));
      this.gallery.scrollTo({ left: next * stride, behavior: 'instant' });
      this.updateGallery();
    }

    setupLightbox() {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      const close = this.dialog.querySelector('.lightbox-close');
      const previous = this.dialog.querySelector('[data-lightbox-previous]');
      const next = this.dialog.querySelector('[data-lightbox-next]');
      this.imageButtons.forEach((button, index) => {
        button.disabled = false;
        this.on(button, 'click', () => {
          this.opener = button;
          this.showImage(index);
          this.dialog.showModal();
          close.focus({ preventScroll: true });
        });
      });
      this.on(close, 'click', () => this.dialog.close());
      this.on(previous, 'click', () => this.showImage(this.imageIndex - 1));
      this.on(next, 'click', () => this.showImage(this.imageIndex + 1));
      this.on(this.dialog, 'keydown', event => {
        if (event.key === 'Tab') {
          const last = this.imageButtons.length > 1 ? next : close;
          if (event.shiftKey && document.activeElement === close) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            close.focus();
          }
        } else if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
          this.showImage(this.imageIndex + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.on(this.dialog, 'click', event => {
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
        }
      });
      this.on(this.dialog, 'close', () => this.opener?.focus({ preventScroll: true }));
    }

    showImage(index) {
      if (index < 0 || index >= this.imageButtons.length) return;
      this.imageIndex = index;
      const source = this.imageButtons[index];
      const image = this.dialog.querySelector('.lightbox-image');
      image.src = source.querySelector('img').currentSrc || source.querySelector('img').src;
      const fullImage = new Image();
      fullImage.onload = () => {
        if (this.isConnected && this.imageIndex === index) image.src = fullImage.src;
      };
      fullImage.src = source.dataset.fullSrc;
      image.alt = source.querySelector('img').alt;
      this.dialog.querySelector('[data-lightbox-position]').textContent = this.positionLabel(index + 1, index + 1, this.imageButtons.length);
      this.dialog.querySelector('[data-lightbox-previous]').setAttribute('aria-disabled', String(index === 0));
      this.dialog.querySelector('[data-lightbox-next]').setAttribute('aria-disabled', String(index === this.imageButtons.length - 1));
    }

    revealVariantMedia(scrollPage) {
      const mediaId = this.querySelector('[data-purchase]')?.dataset.mediaId;
      const photo = this.photos.find(item => item.dataset.mediaId === mediaId);
      if (!photo) return;
      if (this.horizontal.matches) {
        const { stride } = this.galleryState();
        this.gallery.scrollTo({ left: this.photos.indexOf(photo) * stride, behavior: 'instant' });
      } else if (scrollPage) {
        photo.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      }
    }

    async selectVariant(link, url) {
      this.variantRequest?.abort();
      const request = new AbortController();
      this.variantRequest = request;
      const purchase = this.querySelector('[data-purchase]');
      purchase.setAttribute('aria-busy', 'true');
      purchase.querySelector('[name="add"]').disabled = true;
      const endpoint = new URL(url);
      endpoint.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(endpoint, { signal: request.signal });
        if (!response.ok) throw new Error('Variant response unavailable');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = document.querySelector('[data-purchase]');
        if (!replacement) throw new Error('Variant section missing');
        if (request.signal.aborted || !this.isConnected) return;
        const focusId = link.id;
        purchase.replaceWith(replacement);
        history.pushState({}, '', url);
        this.selectionLocation = url.pathname + url.search;
        this.querySelector('#' + CSS.escape(focusId))?.focus({ preventScroll: true });
        this.querySelector('[data-variant-status]').textContent =
          [replacement.querySelector('[data-price]').textContent.trim(), replacement.querySelector('[data-availability]').textContent.trim()].join('. ');
        this.revealVariantMedia(true);
      } catch (error) {
        // A normal Shopify page request is the safe fallback; never leave a stale variant purchasable.
        if (error.name !== 'AbortError') location.assign(url.href);
      }
    }
  });
}

if (!customElements.get('szponty-recommendations')) {
  customElements.define('szponty-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('.pdp-related')) return;
      this.request = new AbortController();
      this.load();
    }
    disconnectedCallback() { this.request?.abort(); }
    async load() {
      try {
        const response = await fetch(this.dataset.url, { signal: this.request.signal });
        if (!response.ok) return;
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const related = document.querySelector('.pdp-related');
        if (related && this.isConnected) this.replaceChildren(related);
      } catch (error) {
        // Recommendations are optional; the product remains complete if the service is unavailable.
        if (error.name !== 'AbortError') this.hidden = true;
      }
    }
  });
}
